<template>
    <span>
        <q-table
            :id="uuid"
            :data="getFilteredValuesData"
            :columns="final_column"
            row-key="id"
            :class="classes"
            :visible-columns="visibleColumns"
            :pagination.sync="pagination"
            :rows-per-page-options="paginationCountOptions"
            :separator="separator"
            :dense="dense"
            :dark="dark"
            :flat="flat"
            :bordered="bordered"
            :square="square"
            :selection="selection_prop"
            :selected.sync="selected_prop"
            :filter="filter"
            :loading="showLoading"
            ref="quasarTable"
        >
            <!-- Loading -->
            <template v-slot:loading>
                <q-inner-loading showing color="primary" />
            </template>

            <!-- Table Header -->
            <template v-slot:header="props">
                <q-tr :props="props">
                    <q-th auto-width v-if="selection_prop !== 'none'">
                        <q-checkbox v-if="props.multipleSelect" v-model="props.selected" indeterminate-value="some" />
                    </q-th>

                    <q-th auto-width v-if="hasExpandRow"/>

                    <q-th :props="props" @hover.native.stop v-for="col in props.cols" :key="col.name">
                        <div class="row inline">
                            <div class="column">
                                <p>{{ col.label }}</p>
                            </div>

                            <div class="column">
                                <q-btn
                                    flat
                                    dense
                                    size="sm"
                                    icon="fa fa-filter"
                                    class="q-ml-xs"
                                    v-if="header_filter && (typeof col.filter === 'undefined' || col.filter)"
                                    @click.stop
                                    :data-cy="$UI.Button.Admin.FlexibleTable.FilterButton"
                                >
                                    <q-icon name="fas fa-asterisk" color="red" style="font-size: 7px;" v-if="selectedColumnOptions[col.field] && (selectedColumnOptions[col.field].length > 0 || Object.keys(selectedColumnOptions[col.field]).length > 0)"></q-icon>

                                    <q-menu class="limitied-menu" :data-cy="$UI.Element.Admin.FlexibleTable.FiltrationPopup.Body">
                                        <q-space />

                                        <div class="q-pa-sm" style="max-width: 300px" :style="[dateBasedFilter.includes(col.filterType) ? {'min-width': '265px'} : false]">
                                            <q-select
                                                v-if="!col.filterType || selectBasedFilter.includes(col.filterType)"
                                                map-options
                                                multiple
                                                emit-value
                                                outlined
                                                dense
                                                :value="selectedColumnOptions[col.field]"
                                                @input="value => onColumnOptionsChanged(col.field, value)"
                                                :options="column_options[col.field]"
                                                style="width: 225px !important;"
                                                :popup-content-class="'hoc-filtration-popup-menu'"
                                                :data-cy="$UI.Button.Admin.FlexibleTable.FiltrationPopup.Select"

                                            />

                                            <q-input readonly outlined dense :value="getDateFilter(selectedColumnOptions[col.field])" v-if="col.filterType && dateBasedFilter.includes(col.filterType)">
                                                <template v-slot:prepend>
                                                    <q-icon name="event" class="cursor-pointer">
                                                        <q-popup-proxy transition-show="scale" transition-hide="scale">
                                                            <q-date
                                                                mask="YYYY-MM-DD"
                                                                range
                                                                :value="selectedColumnOptions[col.field]"
                                                                @input="value => onColumnOptionsChanged(col.field, value)"
                                                            />
                                                        </q-popup-proxy>
                                                    </q-icon>
                                                </template>
                                            </q-input>

                                            <q-input
                                                outlined
                                                dense
                                                type="number"
                                                step="any"
                                                v-if="col.filterType && numberBasedFilter.includes(col.filterType)"
                                                :value="selectedColumnOptions[col.field]" 
                                                @input="value => onColumnOptionsChanged(col.field, value)"
                                            />
                                            
                                        </div>

                                        <div class="q-pa-sm">
                                           <q-btn
                                               color="primary"
                                               class="text-capitalize full-width"
                                               size="sm"
                                               unelevated
                                               v-close-popup
                                               @click="onColumnOptionsChanged(col.field, [])"
                                               label="Clear"
                                           />
                                        </div>
                                    </q-menu>
                                </q-btn>
                            </div>
                        </div>
                    </q-th>

                    <q-th v-if="actionButtons"></q-th>
                </q-tr>

                <q-tr :props="props" class="ignore-elements" v-if="columns_filter">
                    <q-th auto-width v-if="selection_prop !== 'none'"></q-th>

                    <q-th :key="col.name" v-for="col in props.cols" style="padding: 0 0 0 0;">
                        <q-input
                            v-if="!col.hasOwnProperty('filter_type') || col.filter_type === 'text'"
                            dense
                            class="q-pl-xs q-pr-xs"
                            filled
                            v-model="filter_data[col.field]"
                        >
                            <template v-if="filter_data[col.field]" v-slot:append>
                                <q-icon name="cancel" @click.stop="filter_data[col.field] = ''" class="cursor-pointer"/>
                            </template>
                        </q-input>



                        <q-select
                            v-if="col.hasOwnProperty('filter_type') && col.filter_type === 'select'"
                            map-options
                            multiple
                            emit-value
                            filled
                            dense
                            :value="selectedColumnOptions[col.field]"
                            @input="value => onColumnOptionsChanged(col.field, value)"
                            :options="column_options[col.field]"
                        >
                            <template v-slot:append>
                                <q-icon
                                    v-if="selectedColumnOptions[col.field].length > 0"
                                    name="close"
                                    @click.stop="onColumnOptionsChanged(col.field, [])"
                                    class="cursor-pointer"
                                />
                            </template>
                        </q-select>
                    </q-th>
                </q-tr>
            </template>
            <!--/ Table Header -->

            <!-- Table top -->
            <template v-slot:top>
                <div class="hoc-quasar-table__top-container">
                    <div class="hoc-quasar-table__top-container-left">
                        <div v-if="withColumnsConfig" class="hoc-quasar-table__columns-select">
                            <q-select
                                :value="visibleColumns"
                                multiple
                                outlined
                                dense
                                :display-value="$t('general.columns', 'Columns')"
                                emit-value
                                map-options
                                options-dense
                                :options="columns"
                                option-value="name"
                                @add="onAddVisibleColumn"
                                @remove="onRemoveVisibleColumn"
                                :data-cy="$UI.Button.Admin.FlexibleTable.Views.VisibleColumns"
                                :popup-content-class="'hoc-visible-columns-menu'"
                            >
                                <template v-slot:option="{opt, itemProps, itemEvents, selected}">
                                        <q-item
                                            v-bind="itemProps"
                                            v-on="itemEvents"
                                        >
                                            <q-item-section>
                                                <q-item-label>{{opt.label}}</q-item-label>
                                            </q-item-section>

                                             <q-item-section side>
                                                <q-icon
                                                    v-if="selected"
                                                    size="xs"
                                                    name="fal fa-check"
                                                />
                                            </q-item-section>
                                        </q-item>
                                    </template>
                            </q-select>
                        </div>
                    </div>
                    <div class="hoc-quasar-table__top-container-right">
                        <div class="hoc-quasar-table__views-container" v-if="enableViews && viewOptionList !== undefined && (selectedView !== undefined && selectedView !== null)">
                            <div class="hoc-quasar-table__columns-select" :data-cy="$UI.Button.Admin.FlexibleTable.Views.ViewList">
                                <q-select
                                    v-model="selectedView"
                                    outlined
                                    :options="viewOptionList"
                                    dense
                                    option-label="name"
                                >
                                    <template v-slot:option="{opt, itemProps, itemEvents}">
                                        <q-item
                                            v-bind="itemProps"
                                            v-on="itemEvents"
                                            class="view-option"
                                            :data-cy="$UI.Button.Admin.FlexibleTable.Views.ViewListItem"
                                        >
                                            <q-item-section>
                                                <q-item-label lines="1">{{opt.name}}</q-item-label>
                                            </q-item-section>

                                             <q-item-section side>
                                                <div class="q-gutter-xs">
                                                    <q-icon
                                                        v-if="(typeof opt.id !== 'undefined')"
                                                        @click.stop="() => onToggleViewDefaultValue(opt)"
                                                        class="view-option__icon view-option__star-icon"
                                                        :class="opt.default ? 'view-option__star-icon--selected' : null"
                                                        size="xs"
                                                        :name="opt.default ? 'fas fa-star' : 'fal fa-star'"
                                                        :data-cy="$UI.Button.Admin.FlexibleTable.Views.DefaultIcon"
                                                    />

                                                    <q-icon
                                                        v-if="(typeof opt.id !== 'undefined')"
                                                        @click.stop="() => onViewOptionDelete(opt)"
                                                        class="view-option__icon view-option__delete-icon"
                                                        size="xs"
                                                        name="fal fa-trash-alt"
                                                        :data-cy="$UI.Button.Admin.FlexibleTable.Views.DeleteIcon"
                                                    />
                                                </div>
                                            </q-item-section>
                                        </q-item>
                                    </template>
                                    <template v-slot:append>
                                        <q-icon
                                            v-if="(typeof selectedView.id !== 'undefined')"
                                            @click.stop="() => onToggleViewDefaultValue(selectedView)"
                                            class="view-option__icon view-option__star-icon"
                                            :class="selectedView.default ? 'view-option__star-icon--selected' : null"
                                            size="xs"
                                            :name="selectedView.default ? 'fas fa-star' : 'fal fa-star'"
                                            :data-cy="$UI.Button.Admin.FlexibleTable.Views.DefaultIcon"
                                        >
                                            <q-tooltip :disable="$q.platform.is.mobile" v-close-popup>
                                                {{$t('flexibleTable.makeDefaultButton', 'Default view')}}
                                            </q-tooltip>
                                        </q-icon>
                                    </template>
                                </q-select>
                            </div>

                            <q-btn
                                padding="xs"
                                class="bg-primary text-white fz-11"
                                icon="fal fa-save"
                                @click="$emit('onSaveSelectedView')"
                                :disabled="selectedView && !hasDataChanged(selectedView)"
                                :data-cy="$UI.Button.Admin.FlexibleTable.Views.SaveCurrentView"
                            >
                                <q-tooltip :disable="$q.platform.is.mobile" v-close-popup>
                                    {{$t('flexibleTable.saveViewButton', 'Save view')}}
                                </q-tooltip>
                            </q-btn>
                            <q-btn
                                padding="xs"
                                class="bg-primary text-white fz-11"
                                icon="fal fa-plus"
                                @click="$emit('onShowCreateViewModal')"
                                :data-cy="$UI.Button.Admin.FlexibleTable.Views.CreateNewView"
                            >
                                <q-tooltip :disable="$q.platform.is.mobile" v-close-popup>
                                    {{$t('flexibleTable.createViewButton', 'Create new view')}}
                                </q-tooltip>
                            </q-btn>
                        </div>
                        <q-input outlined  v-if="global_search" borderless dense debounce="300" v-model="filter" class="q-mr-md" :placeholder="$t('general.search', 'Search')">
                            <template v-slot:append>
                                <q-icon name="search"/>
                            </template>
                        </q-input>

                        <!-- <q-select
                            class=""
                            outlined
                            dense
                            v-model="selected_group_by_filed"
                            v-if="groupby_filter"
                            :options="gorupby_option"
                            style="width: 150px;"
                        ></q-select> -->

                        <q-btn padding="xs" class="bg-grey-2 fz-11" icon="fal fa-file-export" no-caps v-if="excel_download" @click="exportTable('xlsx')" />
                        <q-btn padding="xs" class="bg-primary text-white fz-11" icon="fal fa-file-export" no-caps v-if="csv_download" @click="exportTable('csv')" />
                        <q-btn padding="xs" color="bg-primary text-white fz-11" icon="fal fa-chart-pie" @click="$emit('chartButtonClicked')" v-if="withCharts" />

                        <q-btn
                            v-if="fullscreen"
                            flat
                            round
                            class="q-ml-sm"
                            dense
                            :icon="props.inFullscreen ? 'fullscreen_exit' : 'fullscreen'"
                            @click="props.toggleFullscreen"
                        >
                            <q-tooltip :disable="$q.platform.is.mobile" v-close-popup>
                                {{ props.inFullscreen ? 'Exit Fullscreen' : 'Toggle Fullscreen' }}
                            </q-tooltip>
                        </q-btn>
                    </div>
                </div>
            </template>
            <!--/ Table top -->

            <template v-slot:body="props">
                <q-tr :props="props" v-if="!hasDefaultSlot" @click="$emit('rowWasClicked', props)">
                    <q-td v-if="selection_prop !== 'none'">
                        <q-checkbox color="primary" v-model="props.selected"/>
                    </q-td>

                    <q-td auto-width @click.stop v-if="hasExpandRow">
                        <q-btn round dense @click="props.expand = !props.expand" :icon="props.expand ? 'remove' : 'add'" />
                    </q-td>

                    <q-td v-for="(col, col_index) in props.cols" :key="col.name" :props="props">
                        <q-btn
                            size="sm"
                            color="accent"
                            round
                            dense
                            @click="props.expand = !props.expand"
                            class="q-mr-sm"
                            :icon="props.expand ? 'remove' : 'add'"
                            v-if="groupby_filter && selected_group_by_filed['value'] !== '' && col_index === 0"
                        />
                        <span v-if="col.html" v-html="col.field(props.row)"></span>
                        <span v-else>
                            {{ typeof col.field !== 'function' ? props.row[col.field] : col.field(props.row) }}
                        </span>
                    </q-td>

                    <q-td v-if="actionButtons">
                        <template>
                            <slot name="actions" v-bind:props="props"></slot>
                        </template>
                    </q-td>
                </q-tr>

                <!-- <q-tr v-if="groupby_filter &&  selected_group_by_filed['value'] !== ''" v-show="props.expand" :props="props">
                    <q-td :colspan="2">
                        <q-table
                            hide-bottom
                            row-key="id"
                            :data="sub_grouped_data[props.row.name]"
                            :columns="columns"
                            :pagination="group_pagination"
                        >
                            <q-tr slot="header" slot-scope="props">
                                <q-th v-for="col in props.cols" :key="col.name" v-if="col.field !== selected_group_by_filed" :props="props">
                                    {{ col.label }}
                                </q-th>
                            </q-tr>

                            <template slot="body" slot-scope="props">
                                <q-tr :props="props">
                                    <q-td :key="col.name" v-if="col.field !== selected_group_by_filed" v-for="col in props.cols" :props="props">
                                        {{ props.row[col.field] }}
                                    </q-td>
                                </q-tr>
                            </template>
                        </q-table>
                    </q-td>
                </q-tr> -->

                <q-tr v-if="props.expand && hasExpandRow" :props="props">
                    <q-td colspan="100%">
                        <table>
                            <thead>
                                <tr v-if="props.row.additionalData && props.row.additionalData[0]">
                                    <th v-for="(charge, key, cIndex) in props.row.additionalData[0]">{{ $t(`general.${key}`, key) | capitalize }}</th>
                                </tr>
                            </thead>

                            <tbody>
                                <tr v-for="(charge, cIndex) in props.row.additionalData" :key="cIndex">
                                    <td v-for="(data, dIndex) in charge" :key="dIndex">{{ data }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </q-td>
                </q-tr>

                <slot name="body" v-bind:row="props.row" v-if="hasDefaultSlot"></slot>
            </template>
        </q-table>
    </span>
</template>

<script>

import Sortable from 'sortablejs';
import {exportFile, uid} from 'quasar';
import { isEmpty, cloneDeep, merge, mergeWith, isArray, concat, without, isEqual } from 'lodash';
import { joinedValuesSeparator, paginationOptions } from '@/ts/DefaultTableConfigs';
import { Pagination } from 'swiper/js/swiper.esm';

function wrapCsvValue(val, formatFn) {
    let formatted = formatFn !== void 0
        ? formatFn(val)
        : val;

    formatted = formatted === void 0 || formatted === null ? '' : String(formatted);
    formatted =  formatted.replace(/<(.|\n)*?>/g, '');
    formatted = formatted.split('"').join('""');

    /**
     * Excel accepts \n and \r in strings, but some other CSV parsers do not
     * Uncomment the next two lines to escape new lines
     */
    // .split('\n').join('\\n')
    // .split('\r').join('\\r')

    return `"${formatted}"`;
}

export default {
    name: 'Grid',
    props: [
        'data',
        'columns',
        'file_name',
        'csv_download',
        'excel_download',
        'columns_filter',
        'header_filter',
        'draggable',
        'classes',
        'separator',
        'dense',
        'dark',
        'flat',
        'bordered',
        'square',
        'selection',
        'selected',
        'fullscreen',
        'global_search',
        'groupby_filter',
        //'pagination',
        'actionButtons',
        'withCharts',
        'withColumnsConfig',
        'showLoading',
        'viewOptionList',
        'selectedViewProp',
        'enableViews',
        'hasExpandRow',
    ],
    data() {
        return {
            filter_data: {},
            uuid: '',


            //Needs to come from props
            column_options: {},
            //column_options_selected: {},
            filter_flags: {},
            defaultPagination: {
                page: 1,
                rowsPerPage: 20,
                sortBy: null,
                descending: false
            },
            selection_prop: '',
            name: '',
            selected_prop: [],
            filter: '',
            //gorupby_option: [],
            group_pagination: {
                rowsPerPage: 0
            },
            grouped_column: [
                {
                    name: 'Grouped',
                    required: true,
                    label: 'Grouped Column Values',
                    align: 'left',
                    field: 'name',
                    sortable: true
                }
            ],
            sub_grouped_data: {},
            sub_grouped_columns: [],
            selected_group_by_filed: {"label": 'Group By Field', "value": ''},
            final_column: [],
            selectBasedFilter: ['string', 'array'],
            dateBasedFilter: ['date'],
            numberBasedFilter: ['max-value','max-value-optional','min-value', 'min-value-optional'],
            small: false,

            newViewName: ''
        };
    },
    computed: {
        selectedColumnOptions: {
            get: function () {
                return (this.selectedView && this.selectedView.config && this.selectedView.config.columnOptions) ? this.selectedView.config.columnOptions : {}
            }
        },
        visibleColumns: {
            get: function () {
                return (this.selectedView && this.selectedView.config && this.selectedView.config.visibleColumns) ? this.selectedView.config.visibleColumns : []
            }
        },
        selectedView: {
            get: function () {
                return this.selectedViewProp;
            },
            set: function (view) {
                const viewCopy = cloneDeep(view);
                this.$emit('onViewSelected', viewCopy)
            }
        },
        paginationFromView: {
            get: function () {
                return (this.selectedView && this.selectedView.config && this.selectedView.config.pagination) ? this.selectedView.config.pagination : {}
            }
        },
        pagination: {
            get: function () {
                return {
                    ...this.defaultPagination,
                    ...this.paginationFromView
                }
            },
            set: function (props) {
                this.defaultPagination.page = props.page;
                this.defaultPagination.rowsPerPage = props.rowsPerPage;
                const paginationObject = props.sortBy ? {sortBy: props.sortBy, descending: props.descending} : {};

                //Checks if the change in sortBy and descending order is equal to the state in teh view
                if(!isEqual(paginationObject, this.paginationFromView)){
                    this.onSortingChange(paginationObject)
                }
            }
        },
        hasDataChanged() {
           return (view) => {
               if(view.id === undefined){
                   return false;
               }
               const listView = this.viewOptionList.find((listView) => {
                   return listView.id === view.id
               });
               if(listView){
                   return !isEqual(view.config, listView.config);
               }
               return false;
           }
        },

        paginationCountOptions() {
            return paginationOptions;
        },
        getFilteredData() {
            let table_columns = this.final_column.map(item => item.field);

            return this.data.filter(item => {
                let i = '';

                for (i = 0; i < table_columns.length; i++) {
                    if (this.filter_data[table_columns[i]] === '') {
                        continue;
                    }

                    if (table_columns[i] in this.filter_data && item[table_columns[i]].toString().toLowerCase().indexOf(this.filter_data[table_columns[i]].toLowerCase()) === -1) {
                        return false;
                    }
                }

                return true;
            });
        },
        getFilteredValuesData() {
            //this.column_options_selected = Object.assign({}, this.selectedColumnOptions);

            let table_Data = this.getFilteredData.filter(item => {
                let i = '';
                for (i = 0; i < this.columns.length; i++) {
                    if (!this.selectedColumnOptions.hasOwnProperty([this.columns[i].field]) || this.selectedColumnOptions[this.columns[i].field].length === 0) {
                        continue;
                    }

                    if (typeof this.columns[i].filterType !== 'undefined' && this.dateBasedFilter.includes(this.columns[i].filterType)) {
                        if (!item[this.columns[i].field]) {
                            return false
                        }

                        if (Array.isArray(this.selectedColumnOptions[this.columns[i].field])) {
                            continue;
                        }

                        let startValue = 0;
                        let endValue   = 0;
                        let rowDate    = this.$moment(item[this.columns[i].field], this.columns[i].dateFormat);

                        if (!rowDate.isValid()) {
                            return false;
                        }

                        rowDate  = rowDate.valueOf();

                        if (typeof this.selectedColumnOptions[this.columns[i].field] === 'object') {
                            startValue = this.$moment(this.selectedColumnOptions[this.columns[i].field].from).startOf('day').valueOf();
                            endValue   = this.$moment(this.selectedColumnOptions[this.columns[i].field].to).endOf('day').valueOf();
                        }

                        if (typeof this.selectedColumnOptions[this.columns[i].field] === 'string') {
                            startValue = this.$moment(this.selectedColumnOptions[this.columns[i].field]).startOf('day').valueOf();
                            endValue   = this.$moment(this.selectedColumnOptions[this.columns[i].field]).endOf('day').valueOf();
                        }

                        if (!(rowDate >= startValue && rowDate <= endValue)) {
                            return false;
                        }
                    }

                    if (typeof this.columns[i].filterType !== 'undefined' && this.columns[i].filterType === 'array') {
                        if (
                            !this.selectedColumnOptions[this.columns[i].field]
                                .some(column_value => item[this.columns[i].field]
                                .toString()
                                .toLowerCase()
                                .split(joinedValuesSeparator)
                                .includes(column_value))
                        )
                            return false;
                    }

                    if (typeof this.columns[i].filterType !== 'undefined' && this.numberBasedFilter.includes(this.columns[i].filterType)) {
                        
                        let filterType = this.columns[i].filterType;
                        if ((filterType === 'max-value' || filterType === 'min-value') && !item[this.columns[i].field]) {
                            // Don't allow undefined values in the result rows
                            return false;
                        } else if ((filterType === 'max-value-optional' || filterType === 'min-value-optional') && !item[this.columns[i].field]) {
                            // Allow for undefined values in the result rows
                            continue;
                        }

                        let filterValue = Number(this.selectedColumnOptions[this.columns[i].field]);
                        let rowValue = Number(item[this.columns[i].field].valueOf());
                        
                        if (filterType === 'max-value' || filterType === 'max-value-optional') {
                            if (filterValue < rowValue) {
                                return false;
                            }
                        }

                        if (filterType === 'min-value' || filterType === 'min-value-optional') {
                            if (filterValue > rowValue) {
                                return false;
                            }
                        }

                    }

                    if (typeof this.columns[i].filterType === 'undefined') {
                        if (this.selectedColumnOptions[this.columns[i].field].indexOf(item[this.columns[i].field].toString().toLowerCase()) === -1)
                            return false;
                    }
                }

                return true;
            });

            // if (this.groupby_filter && this.selected_group_by_filed['value'] !== '') {
            //     let grouped_data = this.groupBy(table_Data, this.selected_group_by_filed['value']);

            //     table_Data = [];

            //     Object.keys(grouped_data).filter(item => {
            //         table_Data.push({"name": item});
            //         return item;
            //     });

            //     this.sub_grouped_data = grouped_data;
            // }

            return table_Data;
        },

        hasDefaultSlot() {
            return this.$scopedSlots.hasOwnProperty("body");
        },

    },
    methods: {
        getDateFilter(columnField) {
            if (Array.isArray(columnField) || !columnField) {
                return '';
            }

            if (typeof columnField === 'object') {
                return `${columnField.from} - ${columnField.to}`
            }

            if (typeof columnField === 'string') {
                return columnField;
            }

            return '';
        },
        // columnAdded(option) {
        //     this.$emit('columnAdded', option);
        // },
        // columnRemoved(option) {
        //     this.$emit('columnRemoved', option);
        // },
        exportTable(type) {
            const content = [this.columns.map(col => wrapCsvValue(col.label))].concat(
                this.getFilteredValuesData.map(row => this.columns.map(col => wrapCsvValue(
                    (typeof col.field === 'function' && !col.ignoreFieldForCsv) ? col.field(row) : row[(col.field === void 0 || col.ignoreFieldForCsv) ? col.name : col.field],
                    col.format
                )).join(','))
            ).join('\r\n');

            const status = exportFile(this.file_name + '.' + type, content, 'text/' + type);

            if (status !== true) {
                this.$q.notify({
                    message: 'Browser denied file download...',
                    color: 'negative',
                    icon: 'warning'
                })
            }
        },
        groupBy(array, key) {
            const result = {};
            array.forEach(item => {
                if (!result[item[key]]) {
                    result[item[key]] = []
                }
                result[item[key]].push(item)
            });

            return result;
        },
        Sorting() {
            let dom = document.getElementById(this.uuid);
            const element = dom.querySelector("table tbody");

            Sortable.create(element, {
                disabled: !this.draggable,
                onEnd: (event) => {
                    let tmp = this.data[(event.oldIndex)];
                    this.data[(event.oldIndex)] = this.data[(event.newIndex)];
                    this.data[(event.newIndex)] = tmp;
                },
                onMove: (evt, originalEvent) => {
                    if (evt.related.className === 'ignore-elements q-tr') {
                        return false
                    }
                },
            });
        },
        configTable() {
            this.uuid = uid();

            this.selection_prop = this.selection === undefined ? 'none' : this.selection;
            this.name           = this.file_name === undefined ? 'Download' : this.file_name;
            this.selected_prop  = this.selected === undefined ? [] : this.selected;
            //this.gorupby_option = [{"label": 'Group By Field', "value": ''}];

            this.column_options = {};
            this.columns.filter(item => {
                this.column_options[item.field] = [];
                //this.$set(this.column_options_selected, item.field, []);
                this.filter_flags[item.field] = false;

                // if (item.hasOwnProperty('grouping') && item.grouping) {
                //     this.gorupby_option.push({"label": item.label, "value": item.field});
                // }

                return item;
            });

            this.data.filter(item => {
                this.columns.filter(column => {
                    if (!item[column.field]) {
                        return false;
                    }

                    switch (column.filterType) {
                        case 'array':
                            item[column.field].split(joinedValuesSeparator).forEach(column_value => {
                                this.column_options[column.field].push({
                                    label: column_value.toString(),
                                    value: column_value.toString().toLowerCase().replace(/_/g, '_'),
                                });
                            });
                            break;

                        default:
                            this.column_options[column.field].push({
                                label: item[column.field].toString(),
                                value: item[column.field].toString().toLowerCase().replace(/_/g, '_'),
                            });
                    }
                });
            });


            this.columns.filter(column => {
                this.column_options[column.field] = [...new Map(this.column_options[column.field].map(item => [item['value'], item])).values()];
            });

            this.final_column = this.selected_group_by_filed['value'] !== '' ? this.grouped_column : this.columns;
        },

        customizer (_, srcValue) {
            if (isArray(srcValue)) {
                return srcValue;
            }
        },

        onColumnOptionsChanged(columnName, option) {
            let selectedColumnsOptionsCopy = cloneDeep(this.selectedColumnOptions);
            selectedColumnsOptionsCopy = mergeWith(selectedColumnsOptionsCopy, {[columnName]: option}, this.customizer);
            selectedColumnsOptionsCopy = Object.fromEntries(Object.entries(selectedColumnsOptionsCopy).filter(([_, v]) => !isEmpty(v)));

            let configCopy = cloneDeep(this.selectedView.config);
            configCopy.columnOptions = selectedColumnsOptionsCopy;
            configCopy = Object.fromEntries(Object.entries(configCopy).filter(([_, v]) => !isEmpty(v)));
            this.$emit('onConfigChange', configCopy);
        },

        onAddVisibleColumn(option) {
            let visibleColumnsCopy = cloneDeep(this.visibleColumns);
            visibleColumnsCopy.push(option.value);

            let configCopy = cloneDeep(this.selectedView.config);
            configCopy['visibleColumns'] = visibleColumnsCopy;
            this.$emit('onConfigChange', configCopy);
        },

        onRemoveVisibleColumn(option) {
            let visibleColumnsCopy = cloneDeep(this.visibleColumns);
            visibleColumnsCopy = without(visibleColumnsCopy, option.value);

            let configCopy = cloneDeep(this.selectedView.config);

            if (configCopy.columnOptions && configCopy.columnOptions[option.value]) {
                delete configCopy.columnOptions[option.value];
            }

            configCopy['visibleColumns'] = visibleColumnsCopy;
            configCopy = Object.fromEntries(Object.entries(configCopy).filter(([_, v]) => !isEmpty(v)));
            this.$emit('onConfigChange', configCopy);
        },

        onViewOptionDelete(view) {
            this.$emit('onViewOptionDelete', view);
        },

        onToggleViewDefaultValue(view) {
            this.$emit('onToggleViewDefaultValue', view);
        },

        onSortingChange(pagination) {
            let configCopy = cloneDeep(this.selectedView.config);
            configCopy['pagination'] = pagination;
            configCopy = Object.fromEntries(Object.entries(configCopy).filter(([_, v]) => !isEmpty(v)));
            this.$emit('onConfigChange', configCopy);
        }

    },
    watch: {
        // 'selected_group_by_filed': function () {
        //     this.final_column = this.groupby_filter && this.selected_group_by_filed['value'] !== '' ? this.grouped_column : this.columns;
        // },
        'selected_prop': function () {
            this.$emit('selected-val', this.selected_prop)
        },
        'data': function() {
            this.configTable();
        },
        'columns': function() {
            this.configTable();
        }
    },
    created() {
        this.configTable();
    },
    mounted() {
        this.Sorting();
    },
}
</script>

<style scoped lang="scss">
.view-option {
    padding: 1rem;
}

.view-option__icon {
   color: $snd-shade-of-grey;
   margin: 0 0 0 10px;
}

.view-option__delete-icon {
    &:hover {
      color: $thd-shade-of-grey;
   }
}

.view-option__star-icon {
    &:hover {
      color: $bucktorn-light;
   }
}

.view-option__star-icon--selected {
    color: $bucktorn-light;
}
</style>

